import { useEffect, useMemo, useState } from 'react';
import { Calendar7, Cita, SlotClickData } from '../../components/calendar/Calendar7.component'
import { DisponibilidadModal } from './DisponibilidadModal';
import { Disponibilidad } from '../../data/types/disponibilidad.types';
import { api } from '../../data/api';
import toast from 'react-hot-toast';
import { useDisponibilidadesMedico } from './useCalendario.hook';
import { formatDateToString, getWeekFromDate, parseDateWithoutTimezone, sumarMinutosAHora } from '../../helpers/dates.helper';
import { TurnoStatus } from '../../data/types/turnos.type';
import { useNavigate } from 'react-router-dom';
import CustomModal from '../../components/modal/CustomModal';
import { ButtonVariants, SmartButton } from '../../components/buttons/GralButton';
import { useMedico } from '../../data/hooks/useMedicos.hook';
import { useTurnosMedico } from '../../data/hooks/useTurnos.hook';
import { Spinner } from '../../components/spinner/Spinner.component';

const coloresCeldasFondo = {
	DISPONIBILIDAD: '#C8E6C9',
	TURNO_PENDIENTE: '#90CAF9',
	TURNO_HISTORICO: '#E0E0E0',
	TURNO_IMPAGO: '#a3b31c',
	TURNO_CANCELADO: '#EF9A9A',
	// DISPONIBILIDAD: '#000000',
	// TURNO_PENDIENTE: '#124791',
	// TURNO_HISTORICO: '#10811b',
	// TURNO_IMPAGO: '#a3b31c',
	// TURNO_CANCELADO: '#a01523',
}

const coloresCeldasTexto = {
	DISPONIBILIDAD: '#000000',
	TURNO_PENDIENTE: '#000000',
	TURNO_HISTORICO: '#000000',
	TURNO_IMPAGO: '#000000',
	TURNO_CANCELADO: '#000000',
}

export const getTituloTurnoByStatus = (status: TurnoStatus) => {
	switch (status) {
		case TurnoStatus.PENDIENTE:
			return 'Turno Pendiente'
		case TurnoStatus.CANCELADO:
			return 'Turno Cancelado'
		case TurnoStatus.COMPLETADO:
			return 'Turno Completado'
		case TurnoStatus.IMPAGO:
			return 'Turno Impago'
		default:
			return 'Turno'
	}
}

export const getColorByTurnoStatus = (status: TurnoStatus, tipo: 'FONDO' | 'TEXTO') => {
	if (tipo === 'FONDO') {
		switch (status) {
			case TurnoStatus.PENDIENTE:
				return coloresCeldasFondo.TURNO_PENDIENTE
			case TurnoStatus.CANCELADO:
				return coloresCeldasFondo.TURNO_CANCELADO
			case TurnoStatus.COMPLETADO:
				return coloresCeldasFondo.TURNO_HISTORICO
			case TurnoStatus.IMPAGO:
				return coloresCeldasFondo.TURNO_IMPAGO
			default:
				return coloresCeldasFondo.TURNO_PENDIENTE
		}
	} else {
		switch (status) {
			case TurnoStatus.PENDIENTE:
				return coloresCeldasTexto.TURNO_PENDIENTE
			case TurnoStatus.CANCELADO:
				return coloresCeldasTexto.TURNO_CANCELADO
			case TurnoStatus.COMPLETADO:
				return coloresCeldasTexto.TURNO_HISTORICO
			case TurnoStatus.IMPAGO:
				return coloresCeldasTexto.TURNO_IMPAGO
			default:
				return coloresCeldasTexto.TURNO_PENDIENTE
		}
	}
}

export const CALENDAR_CONFIG = {
    TIME_SLOT_MINUTES: 15, // Intervalo de tiempo en minutos
    START_HOUR: 0, // Hora de inicio (0 = 12 AM)
    END_HOUR: 23, // Hora de fin (23 = 11 PM)
    DEFAULT_VIEW_HOUR: 8, // Hora por defecto al cargar (8 AM)
    SLOT_HEIGHT: 20, // Altura de cada slot en píxeles
  } as const;
 
export const Calendario = () => {
	const [editDisponibilidad, setEditDisponibilidad] = useState<Disponibilidad | null>(null);
	const [	fechasParams, setFechasParams] = useState<{fechaDesde: string, fechaHasta: string}>({fechaDesde: '', fechaHasta: ''});
	const navigate = useNavigate();
	const { data: medico, isPending: isPendingMedico } = useMedico();
	const [showAdvertencia, setShowAdvertencia] = useState(false);

	useEffect(() => {
		if ( !isPendingMedico && medico && (!medico[0]?.mpToken || !medico[0]?.googleToken)) {
			setShowAdvertencia(true);
		}
	}, [medico]);

	const { 
		data: disponibilidades, 
		isLoading: isLoadingDisponibilidades, 
		isError: isErrorDisponibilidades, 
		error: errorDisponibilidades,
		invalidateDisponibilidades
	} = useDisponibilidadesMedico( fechasParams.fechaDesde, fechasParams.fechaHasta);

	const {
		 data: turnosData, 
		 isLoading: isLoadingTurnos, 
		 isError: isErrorTurnos, 
		 error: errorTurnos 
	} = useTurnosMedico(fechasParams.fechaDesde, fechasParams.fechaHasta);

	const citas = useMemo(() => {
		if (!disponibilidades || isLoadingDisponibilidades) return []

		const citas: Cita[] = []

		turnosData?.forEach(turno => {
			citas.push({
				id: turno.id,
				dia: parseDateWithoutTimezone(turno.fecha).getDay(),
				fecha: turno.fecha,
				horaDesde: turno.hora,
				horaHasta: sumarMinutosAHora(turno.hora, turno.duracion),
				titulo: getTituloTurnoByStatus(turno.statusTurno),				
				tipo: 'CITA',
				colorFondo:  getColorByTurnoStatus(turno.statusTurno, 'FONDO'),
				colorTexto: getColorByTurnoStatus(turno.statusTurno, 'TEXTO')
			})
		})
		disponibilidades?.forEach(disponibilidad => {
			citas.push({
			id: disponibilidad.id,
			dia: parseDateWithoutTimezone(disponibilidad.fecha).getDay(),
			fecha: disponibilidad.fecha,
			horaDesde: disponibilidad.horaDesde,
			horaHasta: disponibilidad.horaHasta,
			titulo: 'Disponible',
			tipo: 'DISPONIBILIDAD',
			colorFondo: coloresCeldasFondo.DISPONIBILIDAD,
			colorTexto: coloresCeldasTexto.DISPONIBILIDAD
			})
		})

		console.log('### Citas:', citas)
		return citas
	}, [disponibilidades, turnosData])

	const onFechaChange = (fecha: Date) => {
		const { lunes, domingo } = getWeekFromDate(fecha)
		console.log('### LUNES Y DOMINGO', fecha, lunes, domingo);
		setFechasParams({fechaDesde: formatDateToString(lunes), fechaHasta: formatDateToString(domingo)})
	}

    const handleSaveDisponibilidad = async () => {
		try {
			console.log('##### Guardando disponibilidad...', editDisponibilidad)
			await api.post('/api/disponibilidades', editDisponibilidad)
			toast.success("Disponibilidad guardada")
			setEditDisponibilidad(null)
			
			// Invalidar después de cerrar el modal para asegurar que se ejecute
			await invalidateDisponibilidades()
		} catch (error) {
			console.error("❌ Error al guardar disponibilidad:", error)
			toast.error("Error al guardar la disponibilidad")
		}
    }

	const handleDeleteDisponibilidad = async () => {
		try {
			console.log('🗑️ Eliminando disponibilidad...', editDisponibilidad?.id)
			await api.delete(`/api/disponibilidades/${editDisponibilidad?.id}`)
			toast.success("Disponibilidad eliminada")
			setEditDisponibilidad(null)
			
			// Invalidar después de cerrar el modal para asegurar que se ejecute
			await invalidateDisponibilidades()
		} catch (error) {
			console.error("❌ Error al eliminar disponibilidad:", error)
			toast.error("Error al eliminar la disponibilidad")
		}
	}

	if (isPendingMedico) return <Spinner />

    return (
        <div style={{ height: '88vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <div style={{ 
          padding: '16px', 
          backgroundColor: '#f8f9fa', 
          borderBottom: '1px solid #dee2e6',
          display: 'flex',
          gap: '12px',
          alignItems: 'center',
          flexShrink: 0
        }}>
          <h3 style={{ margin: 0, color: '#2c3e50' }}>Calendario Semanal</h3>
          {(isLoadingDisponibilidades || isLoadingTurnos) && (
            <div style={{ marginLeft: 'auto', fontSize: '14px', color: '#6c757d' }}>
              Cargando...
            </div>
          )}
          {/* <button 
            // onClick={agregarCita}
            style={{
              padding: '8px 16px',
              backgroundColor: '#28a745',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontWeight: '600'
            }}
          >
            Agregar Cita
          </button> */}
          {/* <div style={{ marginLeft: 'auto', fontSize: '14px', color: '#6c757d' }}>
            {`${medico?.[0]?.nombre} ${medico?.[0]?.apellido}`}
          </div> */}
        </div>
  
        <div style={{ flex: 1, minHeight: 0, overflow: 'hidden', position: 'relative' }}>
          {(isLoadingDisponibilidades || isLoadingTurnos) && (
            <div style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(255, 255, 255, 0.7)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1000
            }}>
              <Spinner />
            </div>
          )}
          <Calendar7
            citas={citas}
            onSlotClick={(slotData: SlotClickData) => {
				console.log("##### slotData", slotData, formatDateToString(slotData.fecha), slotData.hora, slotData.horaFinal)
				setEditDisponibilidad({
					id: 0,
					dni: 1,
					fecha: formatDateToString(slotData.fecha),
					horaDesde: slotData.hora,
					horaHasta: slotData.horaFinal
				})
			}}
			onCitaClick={(cita: Cita) => {
				if (cita.tipo === 'DISPONIBILIDAD') {
					setEditDisponibilidad({
						id: cita.id,
						dni: 1,
						fecha: cita.fecha,
						horaDesde: cita.horaDesde,
						horaHasta: cita.horaHasta
					})
				} else if (cita.tipo === 'CITA') {
					navigate(`/med/turno/${cita.id}`)
				}
			}}
            onFechaChange={onFechaChange}
            config={CALENDAR_CONFIG}
          />
        </div>
        <DisponibilidadModal
            editDisponibilidad={editDisponibilidad}
            setEditDisponibilidad={setEditDisponibilidad}
          	onSaveDisponibilidad={handleSaveDisponibilidad}
			onDeleteDisponibilidad={handleDeleteDisponibilidad}
        />
		{<CustomModal
			showModal={showAdvertencia}
			setShowModal={setShowAdvertencia}
			width='auto'
			height='auto'
		>
			<h2>Advertencia MercadoPago</h2>
			<p style={{ width: '400px'}}>Hemos detectado que no has autorizado al menos un Servicio de Terceros necesario para trabajar con Kaiteki. 
			</p>
			<p style={{ width: '400px'}}>Necesitamos que nos autorice en MercadoPago para poder procesar pagos en su nombre. También necesitamos que nos autorice en Google para poder sincronizar tus citas con Google Calendar. Hacerlo es super simple! En tu Perfil hay un botón que te permite hacerlo en unos clicks.	
			</p>
			<p style={{ width: '400px', fontWeight: 'bold'}}>
				No apareceras en Cartera hasta no finalizar la autorización.
			</p>
			<div style={{ display: 'flex', justifyContent: 'flex-end', width: '400px'}}>
				<SmartButton
					variant={ButtonVariants.DELETE}
					width='90px'
					onClick={() => setShowAdvertencia(false)}
				>
					Entiendo
			  </SmartButton>
			</div>
		</CustomModal>}
      </div>
    )
}